/**
 * Client-side image normalization for the OCR upload.
 *
 * iPhone cameras emit HEIC/HEIF by default and Google Cloud Vision
 * does NOT decode those reliably — every iPhone teacher would hit a
 * provider error on the first photo. We convert client-side via
 * ``heic2any``, which is ~150 KB and dynamically imported so the
 * cost only lands on users who actually drop a HEIC file
 * (.claude/rules/frontend-performance.md §B-2).
 *
 * Non-HEIC images pass through untouched.
 */

import { logger } from "@lib/logger"

const HEIC_MIMES = new Set(["image/heic", "image/heif"])

export async function normalizeImage(input: File): Promise<File> {
  if (!HEIC_MIMES.has(input.type.toLowerCase())) {
    return input
  }

  try {
    const { default: heic2any } = await import("heic2any")
    const result = await heic2any({
      blob: input,
      toType: "image/jpeg",
      quality: 0.92,
    })
    const blob = Array.isArray(result) ? result[0] : result
    const newName = input.name.replace(/\.heic|\.heif/i, ".jpg")
    return new File([blob], newName, {
      type: "image/jpeg",
      lastModified: input.lastModified,
    })
  } catch (err) {
    // HEIC conversion failed (old browser, malformed file, etc.).
    // Surface the original file; the backend MIME check will reject
    // it with a clear ``ocr_unsupported_mime`` error.
    logger.warning("ocr.heic_conversion_failed", {
      error: err instanceof Error ? err.message : String(err),
    })
    return input
  }
}
